import { useTranslation } from "@/hooks";
import { cn } from "@/lib/utils";
import { Icon, Typography } from "@st-marketplace/ui";
import type { ComponentProps, FC } from "react";
import { PAYMENT_METHODS, type PaymentMethod } from "./paymentForm";

type IconName = ComponentProps<typeof Icon>["name"];

const ICONS: Record<PaymentMethod, IconName> = {
	usdt: "coin-line",
	paypal: "paypal-line",
	bank_transfer: "bank-line",
	other: "more-2-line",
};

interface PaymentMethodPickerProps {
	value: PaymentMethod;
	onChange: (value: PaymentMethod) => void;
	/** USDT needs a wallet on the receiving side; it stays visible but disabled. */
	usdtAvailable: boolean;
	disabled?: boolean;
}

/**
 * Cards rather than a segmented control: an unavailable method has to stay in
 * place and explain itself, instead of vanishing and resizing the row.
 */
const PaymentMethodPicker: FC<PaymentMethodPickerProps> = ({
	value,
	onChange,
	usdtAvailable,
	disabled = false,
}) => {
	const { t } = useTranslation();

	return (
		<div role="radiogroup" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
			{PAYMENT_METHODS.map((method) => {
				const isDisabled = disabled || (method === "usdt" && !usdtAvailable);
				const active = value === method && !isDisabled;

				return (
					<button
						key={method}
						type="button"
						role="radio"
						aria-checked={active}
						disabled={isDisabled}
						onClick={() => onChange(method)}
						className={cn(
							"flex h-20 flex-col items-center justify-center gap-1.5 rounded-xl border transition-colors",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-border",
							active
								? "border-primary-border bg-primary-fill/10 text-secondary-text"
								: "border-secondary-border text-secondary-text-100 hover:border-primary-border/60",
							isDisabled &&
								"cursor-not-allowed opacity-40 hover:border-secondary-border",
						)}
					>
						<Icon
							name={ICONS[method]}
							className={active ? "text-primary-100" : "text-secondary-icon"}
						/>
						<Typography variant="body-sm" className="truncate">
							{t(`payment.form.method.${method}`)}
						</Typography>
					</button>
				);
			})}
		</div>
	);
};

export default PaymentMethodPicker;
